import { EtlState } from '@ViewModels';
import { SvgIcon } from '@WebComponents/svgs/icons/SvgIcon';
import * as React from 'react';
import { THEME } from '../../../../../../../web/components-v2/Theme/theme';

const { colors } = THEME;

const DEFAULT_SIZE = 16;

const idleGlyph = <circle cx='8' cy='8' r='6' stroke={colors.icon.gray} strokeWidth='1.5' />;

const suspendedGlyph = <circle cx='8' cy='8' r='6' fill={colors.icon.gray} />;

const runningGlyph = (
	<>
		<circle cx='8' cy='8' r='6' stroke={colors.primary.main} strokeWidth='1.5' />
		<path d='M8 4.5V8l2.4 1.6' stroke={colors.primary.main} strokeWidth='1.5' strokeLinecap='round' />
	</>
);

const warningGlyph = (
	<>
		<path
			d='M8 1.8 15 14H1L8 1.8Z'
			fill={colors.warning.main}
			stroke={colors.warning.dark}
			strokeWidth='1.2'
			strokeLinejoin='round'
		/>
		<path d='M8 6v3.6' stroke={colors.text.main} strokeWidth='1.5' strokeLinecap='round' />
		<circle cx='8' cy='11.7' r='.95' fill={colors.text.main} />
	</>
);

const errorGlyph = (
	<>
		<path d='M5.4 1.4h5.2L14.6 5.4v5.2L10.6 14.6H5.4L1.4 10.6V5.4L5.4 1.4Z' fill={colors.danger.main} />
		<path
			d='M5.9 5.9l4.2 4.2M10.1 5.9l-4.2 4.2'
			stroke={colors.background.light}
			strokeWidth='1.6'
			strokeLinecap='round'
		/>
	</>
);

export const ETL_STATE_LABELS: Record<EtlState, string> = {
	[EtlState.Unknown]: 'Unknown',
	[EtlState.Idle]: 'Idle',
	[EtlState.IdleWithError]: 'Idle with Error',
	[EtlState.InProgress]: 'In Progress',
	[EtlState.Suspended]: 'Suspended',
	[EtlState.SuspendedWithError]: 'Suspended with Error',
};

const glyphFor = (state: EtlState) => {
	if (state === EtlState.InProgress) return runningGlyph;
	if (state === EtlState.IdleWithError) return warningGlyph;
	if (state === EtlState.SuspendedWithError) return errorGlyph;
	if (state === EtlState.Suspended) return suspendedGlyph;
	return idleGlyph;
};

interface IGlyphProps {
	size?: number;
	/** Omit when the icon sits beside its own text label — it is then decorative. */
	label?: string;
}

const Glyph = ({ children, label, size = DEFAULT_SIZE }: React.PropsWithChildren<IGlyphProps>) => (
	<SvgIcon
		className='etl-state-icon'
		width={size}
		height={size}
		viewBox='0 0 16 16'
		fill='none'
		{...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
	>
		{children}
	</SvgIcon>
);

/**
 * Shape, not just color, carries the severity: triangle means the pipeline will retry,
 * octagon means it has stopped. Colorblind admins get the same signal.
 */
export const EtlStateIcon = ({ state, ...props }: IGlyphProps & { state: EtlState }) => (
	<Glyph {...props}>{glyphFor(state)}</Glyph>
);

/**
 * Marks a stage that failed during a past run. Activity records carry only `hadError`,
 * so severity is unknowable here — this is deliberately kind-neutral.
 */
export const StageErrorIcon = (props: IGlyphProps) => <Glyph {...props}>{errorGlyph}</Glyph>;

/** Triangle, matching {@link EtlStateIcon}'s warning state, so severity reads without color. */
export const StageWarningIcon = (props: IGlyphProps) => <Glyph {...props}>{warningGlyph}</Glyph>;
